import React from 'react';
import {
  Banknote,
  CreditCard,
  Clock,
  CheckSquare,
  Square,
  CheckCircle2,
  AlertCircle,
  Send,
  Printer,
  Crown,
} from 'lucide-react';
import CurrencyDisplay from '../CurrencyDisplay';
import { Button } from '../ui/button';
import {
  formatUAEDateDDMMYYYY,
  getUAEDayName,
  getUAETimeHHMM,
  formatDateArabic,
} from '../UAEClock';

const RevenuesTable = ({
  stats,
  isAdmin,
  selectedRevenues,
  toggleRevenueSelection,
  toggleSelectAll,
  handleSendToAdmin,
  sendingToAdmin,
  printingReceipt,
  handlePrintReceipt,
  canPrintReceipt,
  canEditRevenue,
  handleEdit,
  handleDelete,
  getPeriodLabel,
  onAttachLoyalty,
}) => {
  const adminMode = isAdmin();

  const canAttachLoyalty = (rev) => {
    if (rev.loyalty_info) return false;
    try {
      const created = new Date(rev.created_at || rev.date);
      const ageDays = (Date.now() - created.getTime()) / (24 * 60 * 60 * 1000);
      return ageDays < 3;
    } catch {
      return false;
    }
  };

  return (
    <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
      <div className="p-4 border-b border-white/5">
        <h3 className="text-lg font-semibold">سجل الإيرادات - {getPeriodLabel()}</h3>
        <p className="text-sm text-muted-foreground">
          {stats?.revenues_count || 0} عملية | إجمالي:{' '}
          <CurrencyDisplay amount={stats?.total_amount || 0} />
        </p>
      </div>

      {/* Daily Stats Summary */}
      {stats?.daily_stats && Object.keys(stats.daily_stats).length > 0 && (
        <div className="p-4 bg-secondary/20 border-b border-white/5">
          <div className="flex flex-wrap gap-4">
            {Object.entries(stats.daily_stats)
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([day, data]) => (
                <div key={day} className="bg-card rounded-lg p-3 border border-white/10 min-w-[150px]">
                  <div className="text-sm text-muted-foreground">{formatDateArabic(day)}</div>
                  <div className="text-lg font-bold text-primary">
                    <CurrencyDisplay amount={data.amount} />
                  </div>
                  <div className="text-xs text-muted-foreground">{data.clients} زبون</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Selection bar (barber only) */}
      {!adminMode && stats?.revenues?.length > 0 && (
        <div className="p-4 bg-secondary/30 border-b border-white/5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSelectAll}
              className="border-primary/50"
              data-testid="select-all-revenues"
            >
              {selectedRevenues.length ===
              stats.revenues.filter((r) => r.submission_status !== 'sent').length ? (
                <>
                  <CheckSquare className="w-4 h-4 ml-2" /> إلغاء تحديد الكل
                </>
              ) : (
                <>
                  <Square className="w-4 h-4 ml-2" /> تحديد الكل
                </>
              )}
            </Button>
            <span className="text-sm text-muted-foreground">{selectedRevenues.length} إيراد محدد</span>
          </div>
          <Button
            onClick={handleSendToAdmin}
            disabled={selectedRevenues.length === 0 || sendingToAdmin}
            className="bg-green-600 hover:bg-green-700"
            data-testid="send-to-admin-btn"
          >
            <Send className="w-4 h-4 ml-2" />
            {sendingToAdmin ? 'جاري الإرسال...' : 'إرسال للمدير'}
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary/50 border-b border-white/5">
            <tr>
              {!adminMode && <th className="px-4 py-3 text-center text-sm font-semibold w-12">تحديد</th>}
              <th className="px-4 py-3 text-start text-sm font-semibold">التاريخ</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">اليوم</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">الوقت</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">المبلغ</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">الزبائن</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">طريقة الدفع</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">الحالة</th>
              <th className="px-4 py-3 text-start text-sm font-semibold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {!stats?.revenues || stats.revenues.length === 0 ? (
              <tr>
                <td colSpan={adminMode ? '8' : '9'} className="px-4 py-12 text-center text-muted-foreground">
                  لا توجد إيرادات مسجلة لهذه الفترة
                </td>
              </tr>
            ) : (
              stats.revenues.map((transaction) => (
                <tr
                  key={transaction.id}
                  className={`border-b border-white/5 hover:bg-secondary/20 ${
                    selectedRevenues.includes(transaction.id) ? 'bg-primary/10' : ''
                  }`}
                >
                  {!adminMode && (
                    <td className="px-4 py-3 text-center">
                      {transaction.submission_status !== 'sent' ? (
                        <button
                          onClick={() => toggleRevenueSelection(transaction.id)}
                          className="p-1 hover:bg-white/10 rounded"
                          data-testid={`select-revenue-${transaction.id}`}
                        >
                          {selectedRevenues.includes(transaction.id) ? (
                            <CheckSquare className="w-5 h-5 text-primary" />
                          ) : (
                            <Square className="w-5 h-5 text-muted-foreground" />
                          )}
                        </button>
                      ) : (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm">{formatUAEDateDDMMYYYY(transaction.date)}</td>
                  <td className="px-4 py-3 text-sm">{getUAEDayName(transaction.date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      {transaction.receipt_time || getUAETimeHHMM(transaction.date)}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-primary">
                    <CurrencyDisplay amount={transaction.amount} className="text-primary" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-blue-500/20 text-blue-500 px-2 py-1 rounded-full text-sm">
                      {transaction.client_count || transaction.customers || 1} زبون
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {transaction.payment_method === 'cash' ? (
                      <span className="flex items-center gap-1 text-green-500">
                        <Banknote className="w-4 h-4" /> كاش
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-purple-500">
                        <CreditCard className="w-4 h-4" /> مكينة
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {transaction.submission_status === 'sent' ? (
                      <span className="flex items-center gap-1 text-green-500 text-sm">
                        <CheckCircle2 className="w-4 h-4" /> مُرسل
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-500 text-sm">
                        <AlertCircle className="w-4 h-4" /> منتظر
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {canPrintReceipt(transaction) && (
                        <Button
                          onClick={() => handlePrintReceipt(transaction.id)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-500 hover:bg-blue-500/10"
                          disabled={printingReceipt === transaction.id}
                          data-testid={`print-receipt-${transaction.id}`}
                          title={`طباعة إيصال (${transaction.print_count || 0}/2)`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      )}
                      {canAttachLoyalty(transaction) && onAttachLoyalty && (
                        <Button
                          onClick={() => onAttachLoyalty(transaction)}
                          variant="ghost"
                          size="sm"
                          className="text-amber-500 hover:bg-amber-500/10"
                          data-testid={`attach-loyalty-${transaction.id}`}
                          title="ربط بطاقة ولاء بإيراد سابق (≤3 أيام)"
                        >
                          <Crown className="w-4 h-4" />
                        </Button>
                      )}
                      {transaction.loyalty_info?.attachment_status === 'pending' && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400"
                          title="بانتظار اعتماد المدير"
                        >
                          ⏳ {transaction.loyalty_info.points_earned}p
                        </span>
                      )}
                      {transaction.loyalty_info?.attachment_status === 'approved' && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400"
                          title="تمت إضافة النقاط"
                        >
                          ✓ {transaction.loyalty_info.points_earned}p
                        </span>
                      )}
                      {adminMode ? (
                        <Button
                          onClick={() => handleDelete(transaction.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:bg-red-500/10"
                        >
                          حذف
                        </Button>
                      ) : canEditRevenue(transaction) ? (
                        <Button
                          onClick={() => handleEdit(transaction)}
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:bg-primary/10"
                        >
                          تعديل
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">انتهت المدة</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Period Summary Footer */}
      {stats?.revenues && stats.revenues.length > 0 && (
        <div className="p-4 bg-primary/10 border-t border-white/5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-lg font-bold">
              المجموع:{' '}
              <span className="text-primary">
                <CurrencyDisplay amount={stats.total_amount} className="text-primary" />
              </span>
            </div>
            <div className="text-lg">
              {stats.total_clients} زبون | {stats.revenues_count} عملية
            </div>
            <div className="text-sm text-muted-foreground">
              متوسط:{' '}
              <CurrencyDisplay
                amount={stats.total_clients > 0 ? stats.total_amount / stats.total_clients : 0}
              />{' '}
              / زبون
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenuesTable;
